import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Inbox, MessagesSquare, KeyRound, LogOut, Mail, Phone, RefreshCw, Reply, CalendarClock, BellRing } from "lucide-react";
import { Bookings } from "../components/admin/Bookings";
import { TelegramSettings } from "../components/admin/TelegramSettings";
import { ReplyPanel, RepliedBadge } from "../components/admin/ReplyPanel";
import { toast } from "sonner";
import { useAuth, formatApiError } from "../AuthContext";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("ro-RO", { dateStyle: "medium", timeStyle: "short" }) : "");

const Submissions = ({ http }) => {
  const [rows, setRows] = useState(null);
  const [replyId, setReplyId] = useState(null);
  const markReplied = (id, channel) => setRows((r) => r.map((x) => (x.id === id ? { ...x, replied_at: new Date().toISOString(), replied_via: channel } : x)));
  const load = () => http.get("/contact").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (rows === null) return <p className="text-[#4A4A4A]">Se încarcă…</p>;
  return (
    <div data-testid="admin-submissions">
      <div className="flex items-center justify-between mb-4">
        <p className="text-[13px] text-[#4A4A4A]">{rows.length} mesaje primite prin formular</p>
        <button type="button" onClick={load} className="btn-ghost text-[13px] px-3 py-2" data-testid="admin-refresh-submissions"><RefreshCw size={14} /> Reîncarcă</button>
      </div>
      {rows.length === 0 && <p className="soft-card p-6 text-[#4A4A4A]">Niciun mesaj încă.</p>}
      <div className="space-y-3">
        {rows.map((s) => (
          <article key={s.id} className="soft-card p-5" data-testid="admin-submission-row">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <div className="font-display text-xl text-[#002B7F]">{s.name}</div>
                <div className="text-[13px] text-[#4A4A4A] flex flex-wrap gap-x-4 gap-y-1 mt-1">
                  <a href={`mailto:${s.email}`} className="inline-flex items-center gap-1 hover:text-[#F37021]"><Mail size={13} /> {s.email}</a>
                  {s.phone && <a href={`tel:${s.phone}`} className="inline-flex items-center gap-1 hover:text-[#F37021]"><Phone size={13} /> {s.phone}</a>}
                </div>
              </div>
              <div className="text-right text-[12px] text-[#6B7280]">
                <div>{fmt(s.created_at)}</div>
                <div className={s.email_sent ? "text-[#1F5D2D]" : "text-[#A16207]"}>{s.email_sent ? "E-mail trimis" : "Doar salvat"}</div>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {s.service && <div className="inline-block rounded-full bg-[#F5F7FA] border border-[#E6EAF0] px-3 py-1 text-[12px] font-semibold text-[#002B7F]">{s.service}</div>}
              <RepliedBadge s={s} />
            </div>
            <p className="mt-3 text-[14px] text-[#1A1A1A] whitespace-pre-wrap">{s.message}</p>
            <div className="mt-4">
              <button type="button" onClick={() => setReplyId(replyId === s.id ? null : s.id)} className="btn-orange text-[13px] px-4 py-2" data-testid="reply-open">
                <Reply size={14} /> {replyId === s.id ? "Închide" : "Răspunde"}
              </button>
            </div>
            {replyId === s.id && <ReplyPanel submission={s} http={http} onClose={() => setReplyId(null)} onReplied={(ch) => markReplied(s.id, ch)} />}
          </article>
        ))}
      </div>
    </div>
  );
};

const Chats = ({ http }) => {
  const [list, setList] = useState(null);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  useEffect(() => { http.get("/admin/chats").then((r) => setList(r.data)).catch(() => setList([])); }, [http]);
  const open = (sid) => { setActive(sid); http.get(`/admin/chats/${sid}`).then((r) => setMsgs(r.data)); };
  if (list === null) return <p className="text-[#4A4A4A]">Se încarcă…</p>;
  return (
    <div className="grid lg:grid-cols-12 gap-5" data-testid="admin-chats">
      <div className="lg:col-span-5 space-y-2">
        <p className="text-[13px] text-[#4A4A4A] mb-2">{list.length} conversații</p>
        {list.map((c) => (
          <button key={c.session_id} type="button" onClick={() => open(c.session_id)} className={`w-full text-left soft-card p-4 hover:border-[#F37021] ${active === c.session_id ? "border-[#F37021]" : ""}`} data-testid="admin-chat-row">
            <div className="text-[14px] font-semibold text-[#002B7F] line-clamp-2">{c.first_message}</div>
            <div className="text-[12px] text-[#6B7280] mt-1">{fmt(c.last_at)} · {c.count} mesaje</div>
          </button>
        ))}
      </div>
      <div className="lg:col-span-7">
        {!active ? <p className="soft-card p-6 text-[#4A4A4A]">Alege o conversație din stânga.</p> : (
          <div className="soft-card p-5 space-y-3" data-testid="admin-chat-detail">
            {msgs.map((m, i) => (
              <div key={`${m.created_at}-${i}`} className={`chat-msg ${m.role}`} style={{ maxWidth: "100%" }}>
                <div className="text-[10.5px] opacity-70 mb-1">{m.role === "user" ? "Vizitator" : "Asistent"} · {fmt(m.created_at)}</div>
                {m.content}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ChangePassword = ({ http }) => {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Parola a fost schimbată.");
      setCur(""); setNw("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="soft-card p-6 max-w-md space-y-4" data-testid="admin-change-password">
      <label className="block"><span className="text-[13px] font-bold text-[#002B7F]">Parola actuală</span>
        <input type="password" className="input-field mt-2" value={cur} onChange={(e) => setCur(e.target.value)} required data-testid="admin-pw-current" /></label>
      <label className="block"><span className="text-[13px] font-bold text-[#002B7F]">Parola nouă (minim 8 caractere)</span>
        <input type="password" className="input-field mt-2" value={nw} onChange={(e) => setNw(e.target.value)} minLength={8} required data-testid="admin-pw-new" /></label>
      <button type="submit" disabled={busy} className="btn-orange disabled:opacity-70" data-testid="admin-pw-submit"><KeyRound size={16} /> Schimbă parola</button>
    </form>
  );
};

const TABS = [
  { id: "submissions", label: "Mesaje formular", icon: Inbox },
  { id: "bookings", label: "Programări", icon: CalendarClock },
  { id: "chats", label: "Conversații chat", icon: MessagesSquare },
  { id: "notifications", label: "Notificări", icon: BellRing },
  { id: "password", label: "Parolă", icon: KeyRound },
];

export default function AdminDashboard() {
  const { user, logout, http } = useAuth();
  const [tab, setTab] = useState("submissions");
  const [stats, setStats] = useState(null);
  useEffect(() => { if (user) http.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, [user, http]);

  if (user === null) return <div className="min-h-screen grid place-items-center text-[#4A4A4A]">Se verifică sesiunea…</div>;
  if (user === false) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#F5F7FA]" data-testid="admin-dashboard">
      <header className="bg-[#002B7F] text-white">
        <div className="container-x py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src="/brand/marioway-logo.png" alt="Marioway" width={36} height={36} style={{ objectFit: "contain" }} />
            <div>
              <div className="font-display text-lg leading-tight">Panou Admin</div>
              <div className="text-[12px] text-white/70" data-testid="admin-user-email">{user.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" className="text-[13px] text-white/80 hover:text-[#FF8C42] px-3">Vezi site-ul</a>
            <button type="button" onClick={logout} className="btn-orange text-[13px] px-4 py-2" data-testid="admin-logout"><LogOut size={14} /> Ieșire</button>
          </div>
        </div>
      </header>

      <main className="container-x py-8">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="admin-stats">
            {[["Mesaje formular", stats.submissions], ["Programări", stats.bookings ?? 0], ["Conversații chat", stats.chat_sessions], ["E-mail notificări", { ok: "Activ", auth_failed: "Parolă IONOS greșită", not_configured: "Neconfigurat" }[stats.smtp_status]]].map(([k, v]) => (
              <div key={k} className="soft-card p-4"><div className="text-[11px] uppercase tracking-[0.14em] text-[#6B7280] font-bold">{k}</div><div className="font-display text-2xl text-[#002B7F] mt-1">{v}</div></div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap gap-2 mb-6">
          {TABS.map((t) => (
            <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-[13px] font-bold border transition-colors ${tab === t.id ? "bg-[#002B7F] text-white border-[#002B7F]" : "bg-white text-[#002B7F] border-[#E6EAF0] hover:border-[#F37021]"}`} data-testid={`admin-tab-${t.id}`}>
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>
        {tab === "submissions" && <Submissions http={http} />}
        {tab === "bookings" && <Bookings http={http} />}
        {tab === "chats" && <Chats http={http} />}
        {tab === "notifications" && <TelegramSettings http={http} />}
        {tab === "password" && <ChangePassword http={http} />}
      </main>
    </div>
  );
}
